import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError, catchError } from 'rxjs';

import { ConfigService } from '@fcom/core/services/config/config.service';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { mapError, mapErrorForMonitoring } from '@fcom/core/utils/error-utils';

import {
  ConfirmFinnairPlusResponseData,
  JoinFinnairPlusErrorResponseData,
  JoinFinnairPlusRequestData,
  JoinFinnairPlusResponseData,
  VerifyAccountTokenErrorResponseData,
  VerifyFinnairPlusResponseData,
} from '../interfaces/join-finnair-plus.interface';

@Injectable({
  providedIn: 'root',
})
export class JoinService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);

  public joinDialogOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  joinFinnairPlus(joinData: JoinFinnairPlusRequestData): Observable<JoinFinnairPlusResponseData> {
    const apiUrl = this.configService.cfg.loyaltyServices.legacy.loyaltyMemberUrl;
    const apiKey = this.configService.cfg.loyaltyServices.legacy.loyaltyMemberApiKey;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      }),
    };

    return this.http.post<JoinFinnairPlusResponseData>(apiUrl, joinData, httpOptions).pipe(
      catchError((error: unknown) => {
        this.monitoringLogger.error('joinFinnairPlus error', {
          error: mapErrorForMonitoring(error),
        });
        return throwError(() => mapError<JoinFinnairPlusErrorResponseData>(error));
      })
    );
  }

  resendAccountVerificationEmail(memberNumber: string): Observable<VerifyFinnairPlusResponseData> {
    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyMemberResendUrl}`;
    const apiKey = this.configService.cfg.loyaltyServices.legacy.loyaltyMemberApiKey;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      }),
    };

    const body = { memberNumber };

    return this.http.post<VerifyFinnairPlusResponseData>(apiUrl, body, httpOptions).pipe(
      catchError((error: unknown) => {
        this.monitoringLogger.error('resendAccountVerificationEmail error', {
          error: mapErrorForMonitoring(error),
        });
        return throwError(() => error);
      })
    );
  }

  verifyAccountToken(token: string): Observable<ConfirmFinnairPlusResponseData> {
    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyMemberVerifyUrl}?token=${token}`;
    const apiKey = this.configService.cfg.loyaltyServices.legacy.loyaltyMemberApiKey;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      }),
    };

    return this.http.get<ConfirmFinnairPlusResponseData>(apiUrl, httpOptions).pipe(
      catchError((error: unknown) => {
        this.monitoringLogger.error('verifyAccountToken error', {
          error: mapErrorForMonitoring(error),
        });
        return throwError(() => mapError<VerifyAccountTokenErrorResponseData>(error));
      })
    );
  }
}
